/**
 * @fileoverview Snake case key conversion utilities
 *
 * This module provides utilities for converting object keys between camelCase and snake_case formats.
 * It supports recursive conversion of nested objects and arrays, with special handling for edge cases
 * like keys starting with uppercase letters or underscores.
 *
 * Key features:
 * - Recursive conversion of nested objects and arrays
 * - Path-based exclusion for selective conversion
 * - Wildcard pattern support for array exclusions
 * - Special handling for built-in objects (Date, RegExp, Error)
 * - Bidirectional conversion (camelCase ↔ snake_case)
 */

/**
 * Converts an object's keys from camelCase to snake_case recursively.
 *
 * This function traverses an object (including nested objects and arrays) and converts
 * all camelCase keys to snake_case format. It handles special cases like keys starting
 * with uppercase letters and provides path-based exclusion functionality.
 *
 * @param obj - The object to convert. Can be any type, but only objects and arrays are processed.
 * @param excludePaths - Optional array of paths to exclude from snake_case conversion.
 *                      Supports wildcard patterns (e.g., 'slides.*.staticContent').
 * @param currentPath - Internal parameter used for path tracking during recursion.
 *
 * @returns The converted object with snake_case keys, or the original value if not an object.
 *
 * @example
 * ```typescript
 * // Basic usage
 * toSnakeKeys({ firstName: 'John', lastName: 'Doe' })
 * // Returns: { first_name: 'John', last_name: 'Doe' }
 *
 * // With path exclusion
 * toSnakeKeys({
 *   userInfo: { firstName: 'John' },
 *   staticContent: { html: '<div>test</div>' }
 * }, ['staticContent'])
 * // Returns: { user_info: { first_name: 'John' }, static_content: { html: '<div>test</div>' } }
 *
 * // With wildcard patterns
 * toSnakeKeys({
 *   slides: [{ staticContent: { html: '<div>slide1</div>' } }]
 * }, ['slides.*.staticContent'])
 * // Returns: { slides: [{ static_content: { html: '<div>slide1</div>' } }] }
 * ```
 */
export function toSnakeKeys(
  obj: any,
  excludePaths: string[] = [],
  currentPath: string = '',
): any {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  // Handle Date objects and other built-in objects by returning them as-is
  if (obj instanceof Date || obj instanceof RegExp || obj instanceof Error) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item, index) =>
      toSnakeKeys(item, excludePaths, `${currentPath}.*`),
    );
  }

  return Object.keys(obj).reduce((acc, key) => {
    // Convert camelCase key to snake_case
    let snakeKey = key;

    // Special handling for keys starting with a single uppercase letter (e.g., "FirstName")
    // These get a leading underscore to become "_first_name"
    if (/^[A-Z][a-z]/.test(key)) {
      // Add leading underscore and convert the rest normally
      snakeKey =
        '_' +
        key.replace(/([A-Z])/g, (match, letter, index) => {
          return index === 0
            ? letter.toLowerCase()
            : `_${letter.toLowerCase()}`;
        });
    } else {
      // Standard camelCase to snake_case conversion
      // Converts "firstName" -> "first_name", "XMLHttpRequest" -> "x_m_l_http_request"
      snakeKey = key.replace(/([A-Z])/g, (match, letter, index) => {
        return index === 0 ? letter.toLowerCase() : `_${letter.toLowerCase()}`;
      });
    }

    // Build the current path for this key to support path-based exclusions
    const keyPath = currentPath ? `${currentPath}.${key}` : key;
    const snakeKeyPath = currentPath ? `${currentPath}.${snakeKey}` : snakeKey;

    // Check if this key should be excluded from further snake_case conversion
    const shouldExclude = excludePaths.some((path) => {
      // Direct key matches (e.g., 'staticContent')
      if (path === key || path === snakeKey) return true;
      // Full path matches (e.g., 'userInfo.staticContent')
      if (path === keyPath || path === snakeKeyPath) return true;
      // Partial path matches (e.g., 'metadata.rawData' matches 'userInfo.metadata.rawData')
      if (keyPath.endsWith(`.${path}`) || snakeKeyPath.endsWith(`.${path}`))
        return true;
      // Wildcard pattern matches (e.g., 'slides.*.staticContent' matches 'slides.0.staticContent')
      if (path.includes('*')) {
        const pattern = path.replace(/\*/g, '.*');
        return (
          new RegExp(`^${pattern}$`).test(keyPath) ||
          new RegExp(`^${pattern}$`).test(snakeKeyPath)
        );
      }
      return false;
    });

    if (shouldExclude) {
      // Excluded paths: keep the original value unchanged (no recursive conversion)
      // The key itself is still converted to snake_case, but the value is preserved as-is
      acc[snakeKey] = obj[key];
    } else {
      // Normal processing: continue with recursive snake_case conversion
      acc[snakeKey] = toSnakeKeys(obj[key], excludePaths, keyPath);
    }

    return acc;
  }, {} as any);
}

/**
 * Converts an object's keys from snake_case to camelCase recursively.
 *
 * This function is the inverse of `toSnakeKeys` and traverses an object (including
 * nested objects and arrays) to convert all snake_case keys to camelCase format.
 * It handles special cases like keys starting with underscores and multiple underscores.
 *
 * @param obj - The object to convert. Can be any type, but only objects and arrays are processed.
 *
 * @returns The converted object with camelCase keys, or the original value if not an object.
 *
 * @example
 * ```typescript
 * // Basic usage
 * fromSnakeKeys({ first_name: 'John', last_name: 'Doe' })
 * // Returns: { firstName: 'John', lastName: 'Doe' }
 *
 * // With nested objects
 * fromSnakeKeys({
 *   user_info: { first_name: 'John' },
 *   static_content: { html_content: '<div>test</div>' }
 * })
 * // Returns: { userInfo: { firstName: 'John' }, staticContent: { htmlContent: '<div>test</div>' } }
 *
 * // With arrays
 * fromSnakeKeys({
 *   slides: [{ static_content: { html: '<div>slide1</div>' } }]
 * })
 * // Returns: { slides: [{ staticContent: { html: '<div>slide1</div>' } }] }
 * ```
 */
export const fromSnakeKeys = (obj: any): any => {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  // Handle Date objects and other built-in objects by returning them as-is
  if (obj instanceof Date || obj instanceof RegExp || obj instanceof Error) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => fromSnakeKeys(item));
  }

  return Object.keys(obj).reduce((acc, key) => {
    // Convert snake_case key to camelCase
    const camelKey = key
      .replace(/^_+/, '') // Remove leading underscores
      .replace(/_([a-z0-9])/g, (match, letter) => {
        // Convert underscore + letter to uppercase letter
        return letter.toUpperCase();
      });
    acc[camelKey] = fromSnakeKeys(obj[key]);
    return acc;
  }, {} as any);
};
